import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { redirectService } from "@/services/redirectService";
import { Toaster } from "@/components/ui/toaster";
import ProtectedRoute from "@/components/ProtectedRoute";
import { FeatureProtectedRoute } from "@/components/auth/FeatureProtectedRoute";
import AnalyticsPageTracker from "@/components/AnalyticsPageTracker";
import { AuthErrorHandler } from "@/components/auth/AuthErrorHandler";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AgentList from "@/pages/AgentList";
import AgentCreate from "@/pages/AgentCreate";
import AgentEdit from "@/pages/AgentEdit";
import AgentDetail from "@/pages/AgentDetail";
import AIAssistantDetail from "@/pages/AIAssistantDetail";
import OnboardingPage from "@/pages/OnboardingPage";
import PaymentPage from "@/pages/PaymentPage";
import NotFound from "@/pages/NotFound";
import PublicAgentAPI from "@/pages/PublicAgentAPI";
import KnowledgeBase from "@/pages/KnowledgeBase";
import InboxNew from "@/pages/Inbox-new";
import PublicAgentChat from "@/components/PublicAgentChat";
import PublicAgentAuth from "@/components/PublicAgentAuth";
import EdgeFunctionDebug from "@/pages/EdgeFunctionDebug";
import FirecrawlDebug from "@/pages/FirecrawlDebug";
import SettingsPage from "@/pages/settings/SettingsPage";
import AdminDashboardPage from "@/pages/settings/AdminDashboardPage";
import AdminTemplatesPage from "@/pages/settings/AdminTemplatesPage";
import SuperAdminPage from "@/pages/super-admin/SuperAdminPage";
import DashboardPage from "@/pages/super-admin/DashboardPage";
import TemplateManagerPage from "@/pages/super-admin/TemplateManagerPage";
import TemplateDetailPage from "@/pages/super-admin/TemplateDetailPage";
import UserManagementPage from "@/pages/super-admin/UserManagementPage";
import OrganizationsPage from "@/pages/super-admin/OrganizationsPage";
import VectorStoreDebugPage from "@/pages/super-admin/VectorStoreDebugPage";
import SystemSettingsPage from "@/pages/super-admin/SystemSettingsPage";
import SuperAdminAIAssistantsPage from "@/pages/super-admin/AIAssistantsPage";
import AIAssistantFormattingPage from "@/pages/super-admin/AIAssistantFormattingPage";
import PlansPage from "@/pages/super-admin/PlansPage";
import OrganizationDetailPage from "@/pages/super-admin/OrganizationDetailPage";
import PlanDetailPage from "@/pages/super-admin/PlanDetailPage";
import StripeSyncPage from "@/pages/super-admin/StripeSyncPage";
import IntegrationsAdminPage from "@/pages/super-admin/IntegrationsAdminPage";
import UserAIAssistantsPage from "@/pages/AIAssistantsPage";
import PreviewChatBubble from "@/pages/PreviewChatBubble";
import ChatWidgetTester from "@/pages/ChatWidgetTester";
import ContactsPage from "@/pages/Contacts";
import VoiceCalls from "@/pages/VoiceCalls";
import VoiceAgentDetail from "@/pages/VoiceAgentDetail";
import TeamInvitationPage from "@/pages/TeamInvitationPage";
import AcceptInvite from "@/pages/AcceptInvite";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import ThankYouPage from "@/pages/ThankYouPage";
import TrialExpiredPage from "@/pages/TrialExpiredPage";
import StyleGuide from "@/pages/StyleGuide";
import SupportPage from "@/pages/SupportPage";
import HelpPage from "@/pages/HelpPage";
import LibraryPage from "@/pages/LibraryPage";
import FoldersPage from "@/pages/FoldersPage";
import { TiptapTestPage } from "@/pages/TiptapTestPage";
import { PublicAssistantView } from "@/components/public-assistant/PublicAssistantView";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ChatAssistantPage from "@/pages/ChatAssistant";
import PublicLanding from "@/pages/PublicLanding";

import "./App.css";

// Default route handler that waits for AuthContext to determine redirect
const DefaultRouteHandler = () => {
  const { user, organization, isLoading, isWaitingForOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only proceed when auth loading is complete
    // Don't require user immediately as AuthContext might be slow to update
    if (isLoading || isWaitingForOrganization) {
      return;
    }

    // If no user after auth loading is complete, redirect to register
    if (!user) {
      navigate("/register", { replace: true });
      return;
    }

    // Check for new user flags
    const newUserOnboarding =
      sessionStorage.getItem("newUserOnboarding") === "true";
    const pendingNewUserRedirect =
      sessionStorage.getItem("pendingNewUserRedirect") === "true";
    const isNewUser =
      newUserOnboarding || pendingNewUserRedirect || location.state?.isNewUser;

    // Use redirectService to determine where to go
    redirectService
      .getPostAuthRedirect(user, organization, isNewUser)
      .then((decision) => {
        navigate(decision.path, { replace: true });
      })
      .catch((error) => {
        console.error(
          "[DefaultRouteHandler] Error determining redirect:",
          error,
        );
        // Fallback to ai-assistants
        navigate("/ai-assistants", { replace: true });
      });
  }, [
    user,
    organization,
    isLoading,
    isWaitingForOrganization,
    navigate,
    location.state,
  ]);

  // Show loading while determining redirect
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center space-y-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <p className="text-sm text-muted-foreground">
          Setting up your workspace...
        </p>
      </div>
    </div>
  );
};

// Add import for VoiceCallsProvider
import { VoiceCallsProvider } from "./contexts/VoiceCallsContext";

// Add import for InboxProvider
import { InboxProvider } from "./contexts/InboxContext";

// Add import for ContactsProvider
import { ContactsProvider } from "./contexts/ContactsContext";

// Add import for KnowledgeProvider
import { KnowledgeProvider } from "./contexts/KnowledgeContext";

export default function App() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // TODO: Re-implement organization routing logic with OrganizationProvider
  // useEffect(() => {
  //   Organization-based routing logic will be re-added after provider integration
  // }, []);

  if (!isHydrated) {
    return null;
  }

  return (
    <>
      <AnalyticsPageTracker />
      <AuthErrorHandler />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/invite/:token" element={<TeamInvitationPage />} />

        {/* Public facing agent routes - these should be outside the ProtectedRoute */}
        <Route path="/:agentUrl/:publicId" element={<PublicAgentChat />} />
        <Route path="/auth/:publicId" element={<PublicAgentAuth />} />
        <Route
          path="/api/agents/:publicId/public"
          element={<PublicAgentAPI />}
        />

        {/* Public pages */}
        <Route path="/landing" element={<PublicLanding />} />
        <Route
          path="/public/assistant/:publicId"
          element={
            <ErrorBoundary>
              <PublicAssistantView />
            </ErrorBoundary>
          }
        />
        <Route
          path="/public/output/:publicId"
          element={
            <ErrorBoundary>
              <PublicAssistantView />
            </ErrorBoundary>
          }
        />

        {/* Onboarding routes - outside of data contexts since new users don't have data yet */}
        <Route
          path="/user-onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={<Navigate to="/user-onboarding" replace />}
        />
        {/* Payment and trial pages - also outside data contexts for better performance */}
        <Route
          path="/payment"
          element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/thank-you"
          element={
            <ProtectedRoute>
              <ThankYouPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trial-expired"
          element={
            <ProtectedRoute>
              <TrialExpiredPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <VoiceCallsProvider>
                <InboxProvider>
                  <ContactsProvider>
                    <KnowledgeProvider>
                      <Outlet />
                    </KnowledgeProvider>
                  </ContactsProvider>
                </InboxProvider>
              </VoiceCallsProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<DefaultRouteHandler />} />
          <Route
            path="/home"
            element={<Navigate to="/ai-assistants" replace />}
          />
          {/* Organization setup - no longer needed since orgs are created during registration */}
          {/* <Route path="/organization-setup" element={<OrganizationSetup />} /> */}
          <Route
            path="/pricing"
            element={<Navigate to="/settings?tab=subscription" replace />}
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/settings/account"
            element={<Navigate to="/settings?tab=profile" replace />}
          />
          <Route path="/settings/admin" element={<AdminDashboardPage />} />
          <Route
            path="/settings/admin/templates"
            element={<AdminTemplatesPage />}
          />
          <Route path="/super-admin" element={<SuperAdminPage />} />
          <Route path="/super-admin/dashboard" element={<DashboardPage />} />
          <Route
            path="/super-admin/templates"
            element={<TemplateManagerPage />}
          />
          <Route
            path="/super-admin/templates/:id"
            element={<TemplateDetailPage />}
          />
          <Route path="/super-admin/users" element={<UserManagementPage />} />
          <Route
            path="/super-admin/organizations"
            element={<OrganizationsPage />}
          />
          <Route
            path="/super-admin/vector-store-debug"
            element={<VectorStoreDebugPage />}
          />
          <Route
            path="/super-admin/ai-assistants"
            element={<SuperAdminAIAssistantsPage />}
          />
          <Route
            path="/super-admin/ai-assistants/formatting"
            element={<AIAssistantFormattingPage />}
          />
          <Route path="/super-admin/system" element={<SystemSettingsPage />} />
          <Route path="/super-admin/plans" element={<PlansPage />} />
          <Route path="/super-admin/plans/:id" element={<PlanDetailPage />} />
          <Route path="/super-admin/stripe-sync" element={<StripeSyncPage />} />
          <Route
            path="/super-admin/integrations"
            element={<IntegrationsAdminPage />}
          />
          <Route
            path="/super-admin/organizations/:id"
            element={<OrganizationDetailPage />}
          />

          <Route
            path="/organization-settings"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/settings/business"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/settings/website"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/settings/contact"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/instructions"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/settings/general"
            element={<Navigate to="/settings?tab=general" replace />}
          />
          <Route
            path="/settings/appearance"
            element={<Navigate to="/settings?tab=appearance" replace />}
          />

          <Route
            path="/agents"
            element={
              <FeatureProtectedRoute featureType="agents">
                <AgentList />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/agents/create"
            element={
              <FeatureProtectedRoute featureType="agents">
                <AgentCreate />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/agents/:id"
            element={
              <FeatureProtectedRoute featureType="agents">
                <AgentDetail />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/agents/:id/edit"
            element={
              <FeatureProtectedRoute featureType="agents">
                <AgentEdit />
              </FeatureProtectedRoute>
            }
          />
          <Route path="/ai-assistants" element={<UserAIAssistantsPage />} />

          <Route
            path="/ai-assistants/:instanceId"
            element={<AIAssistantDetail />}
          />
          {/* New Chat Assistants route (hidden entry point for now) */}
          <Route
            path="/chat-assistants/:instanceId"
            element={<ChatAssistantPage />}
          />

          {/* Voice Agent Routes */}
          <Route
            path="/voice-agents/:id"
            element={
              <FeatureProtectedRoute featureType="agents">
                <VoiceAgentDetail />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/voice-calls"
            element={
              <FeatureProtectedRoute featureType="agents">
                <VoiceCalls />
              </FeatureProtectedRoute>
            }
          />

          <Route
            path="/inbox"
            element={
              <FeatureProtectedRoute featureType="agents">
                <InboxNew />
              </FeatureProtectedRoute>
            }
          />
          <Route path="/inbox-new" element={<Navigate to="/inbox" replace />} />

          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/folders" element={<FoldersPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/help" element={<HelpPage />} />

          <Route path="/debug/edge-functions" element={<EdgeFunctionDebug />} />
          <Route path="/style-guide" element={<StyleGuide />} />
          <Route path="/tiptap-test" element={<TiptapTestPage />} />
          <Route
            path="/preview-bubble/:publicId"
            element={<PreviewChatBubble />}
          />
          <Route path="/widget-tester" element={<ChatWidgetTester />} />
          <Route
            path="/contacts"
            element={
              <FeatureProtectedRoute featureType="agents">
                <ContactsPage />
              </FeatureProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/debug/edge-functions"
          element={
            <ProtectedRoute>
              <EdgeFunctionDebug />
            </ProtectedRoute>
          }
        />

        <Route
          path="/firecrawl-debug"
          element={
            <ProtectedRoute>
              <FirecrawlDebug />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </>
  );
}
